// src/Admin/pages/Orders/CreateOrder/CreateOrder.tsx

import { useQuery } from '@tanstack/react-query';
import { ProductsTable, OrderTable, OrderForm } from './components';
import OrderSuccess from './components/OrderSuccess';
import { useMemo, useState } from 'react';

import { Product } from '../../../../models/Product/Product';
import { ButtonPrimary } from '../../../components/Button/ButtonPrimary';
import style from './create-order.module.scss';
import cx from 'classnames';
import { useCartContext } from '../../../../context/CartContext/CartContext';
import { Order } from '../../../../models/Order/Order';

const getAllProducts = () => Product.listForOrder();

const CreateOrder = () => {
  const { cart, cartAddProduct, cartRemoveProduct, cartClear } =
    useCartContext();

  const [showModal, setShowModal] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [createdOrderNumber, setCreatedOrderNumber] = useState<string | null>(
    null
  );

  const { data: products, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products-for-order'],
    queryFn: getAllProducts
  });

  const handleCloseModal = () => setShowModal(false);

  const handleAddToOrder = (product: Product) => {
    cartAddProduct(product.id, 1);
  };

  const handleRemoveFromOrder = (product: Product) => {
    cartRemoveProduct(product.id);
  };

  // Use backend‐provided values instead of recalculating
  const rentalDurationInDays = useMemo(
    () => cart?.numberOfDays ?? 0,
    [cart?.numberOfDays]
  );
  const baseSubtotal = useMemo(() => cart?.subtotal ?? 0, [cart?.subtotal]);
  const effectivePriceFactor = useMemo(
    () => cart?.priceFactor ?? 1,
    [cart?.priceFactor]
  );
  const displayTotalPrice = useMemo(
    () => cart?.totalWithFactor ?? baseSubtotal * effectivePriceFactor,
    [cart?.totalWithFactor, baseSubtotal, effectivePriceFactor]
  );

  return (
    <>
      <h1 className="cs-title">Kreiraj Narudžbinu</h1>

      {!orderCreated ? (
        <>
          <h2 className="cs-subtitle">Lista proizvoda</h2>
          <p>Odaberite proizvode koje želite da uključite u porudžbinu:</p>
          <OrderTable />

          <ButtonPrimary type="button" onClick={() => setShowModal(true)}>
            Dodaj Proizvode
          </ButtonPrimary>
          <hr />

          <div className={style.orderInfoWrapper}>
            <h2 className="cs-subtitle">Informacije o porudžbini</h2>
            <div className={style.orderInfo}>
              <div>
                Broj dana:{' '}
                <span className="fw-bold">{rentalDurationInDays}</span>
              </div>
              <div>
                Cena (osnovna):{' '}
                <span className="fw-bold">{baseSubtotal.toFixed(2)} RSD</span>
              </div>
              <div>
                Faktor perioda izdavanja:{' '}
                <span className="fw-bold">
                  x{effectivePriceFactor.toFixed(2)}
                </span>
              </div>
              <div>
                Ukupno:{' '}
                <span className="fw-bold">
                  {displayTotalPrice.toFixed(2)} RSD
                </span>
              </div>
            </div>
          </div>

          <div className={cx(style.modalWrapper, showModal && style.show)}>
            <div className={style.modalContainer}>
              {isLoadingProducts && <p>Učitavanje proizvoda...</p>}
              {!isLoadingProducts && products && (
                <ProductsTable products={products} />
              )}
              {!isLoadingProducts && !products && (
                <p>Nema dostupnih proizvoda.</p>
              )}
              <button
                className={cx(style.modalButton, 'cs-button-primary')}
                onClick={handleCloseModal}
              >
                Sačuvaj
              </button>
            </div>
          </div>

          <hr />
          <h2 className="cs-subtitle">Informacije o kupcu</h2>
          <OrderForm
            onAfterSubmit={({
              orderId,
              orderNumber
            }: {
              orderId: number;
              orderNumber: string;
            }) => {
              setCreatedOrderId(orderId);
              setCreatedOrderNumber(orderNumber);
              cartClear();
              setOrderCreated(true);
            }}
          />
        </>
      ) : (
        <OrderSuccess
          orderId={createdOrderId!}
          orderNumber={createdOrderNumber!}
          onNewOrder={() => setOrderCreated(false)}
        />
      )}
    </>
  );
};

export default CreateOrder;
